import React, { useState, useEffect, useRef } from 'react';
import { BarChart3, TrendingUp, Users, Briefcase, Building2, MapPin, Target, Zap, Award, RefreshCw, PieChart, FileText } from 'lucide-react';
import { statsAPI } from '../lib/api';

// Mapeamento de nomes bonitos para as categorias
const CATEGORY_NAMES = {
  vaga: 'Vagas de Emprego',
  noticia: 'Notícias',
  eleicao: 'Eleições / Editais',
  concurso: 'Concursos Públicos',
  estagio: 'Estágios',
  curso: 'Cursos',
  freelance: 'Freelance',
  voluntario: 'Voluntariado'
};

// --- COMPONENTE: CONTADOR ANIMADO ---
const CountUp = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    let startTime;
    const endValue = parseInt(end, 10) || 0;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeOut * endValue));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration]);

  return <span ref={countRef}>{count.toLocaleString('pt-BR')}</span>;
};

// --- CARD DE ESTATÍSTICA ---
const StatCard = ({ icon: Icon, value, label, trend, gradient, delay }) => (
  <div 
    className={`relative overflow-hidden rounded-2xl p-5 md:p-6 ${gradient} transform transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl opacity-0 animate-in fade-in slide-in-from-bottom-4 fill-mode-forwards shadow-lg`}
    style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
    <div className="absolute -right-6 -bottom-6 text-white/10 rotate-12 transform scale-150 pointer-events-none">
        <Icon size={100} />
    </div>
    <div className="relative z-10">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs md:text-sm text-white/90 mb-1 font-bold uppercase tracking-wide">{label}</p>
          <p className="text-3xl md:text-4xl font-bold text-white font-heading">
            <CountUp end={value} />
          </p>
          {trend !== undefined && (
            <p className="text-xs md:text-sm text-white flex items-center gap-1 mt-3 bg-white/20 w-fit px-2 py-1 rounded-lg backdrop-blur-sm">
              <TrendingUp className="w-3 h-3 md:w-4 md:h-4" />
              <span className="font-semibold">{trend > 0 ? '+' : ''}{trend}%</span>
            </p>
          )}
        </div>
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-md shadow-inner">
          <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2} />
        </div>
      </div>
    </div>
  </div>
);

// --- GRÁFICO DE BARRAS (Para Cidades) ---
const InteractiveBarChart = ({ data, title, icon: Icon }) => {
  const [hoveredKey, setHoveredKey] = useState(null);
  if (!data || Object.keys(data).length === 0) return null;

  const maxValue = Math.max(...Object.values(data));
  const colors = ['bg-teal-500', 'bg-emerald-500', 'bg-cyan-500', 'bg-blue-500', 'bg-indigo-500', 'bg-violet-500'];

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-5 md:p-6 shadow-sm border border-slate-100 dark:border-slate-700/50 h-full flex flex-col">
      <h3 className="text-base md:text-lg font-semibold font-heading text-slate-800 dark:text-white mb-6 flex items-center gap-2">
        <Icon className="w-5 h-5 text-teal-500" /> {title}
      </h3>
      <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar">
        {Object.entries(data).sort((a,b) => b[1] - a[1]).map(([key, value], index) => {
          const isHovered = hoveredKey === key;
          const percentage = (value / maxValue) * 100;
          return (
            <div key={key} className="group cursor-pointer" onMouseEnter={() => setHoveredKey(key)} onMouseLeave={() => setHoveredKey(null)}>
              <div className="flex justify-between text-sm mb-1.5 items-end">
                <span className={`font-medium text-xs md:text-sm transition-colors ${isHovered ? 'text-teal-600 dark:text-teal-400' : 'text-slate-600 dark:text-slate-400'}`}>
                    {CATEGORY_NAMES[key] || key}
                </span>
                <span className={`font-bold text-xs md:text-sm transition-colors ${isHovered ? 'text-teal-600 dark:text-teal-400 scale-110' : 'text-slate-800 dark:text-white'}`}>
                    {value}
                </span>
              </div>
              <div className="h-2.5 md:h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden relative">
                <div className={`h-full ${colors[index % colors.length]} rounded-full transition-all duration-1000 ease-out relative`} style={{ width: `${percentage}%` }}>
                    <div className={`absolute inset-0 bg-white/30 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- GRÁFICO DE PIZZA (Para Áreas e Categorias) ---
const InteractivePieChart = ({ data, title, icon: Icon }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  if (!data || Object.keys(data).length === 0) return null;

  const total = Object.values(data).reduce((a, b) => a + b, 0);
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]); // Ordenar do maior para o menor
  const colors = ['#14b8a6', '#0d9488', '#0f766e', '#115e59', '#134e4a', '#84cc16', '#eab308', '#f97316']; 
  
  let cumulativePercent = 0;

  const getCoordinatesForPercent = (percent) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-5 md:p-6 shadow-sm border border-slate-100 dark:border-slate-700/50 flex flex-col">
      <h3 className="text-base md:text-lg font-semibold font-heading text-slate-800 dark:text-white mb-6 flex items-center gap-2">
        <Icon className="w-5 h-5 text-teal-500" /> {title}
      </h3>
      <div className="flex flex-col sm:flex-row items-center gap-8 justify-center sm:justify-start h-full">
        <div className="relative w-48 h-48 md:w-56 md:h-56 flex-shrink-0">
          <svg viewBox="-1.2 -1.2 2.4 2.4" className="w-full h-full transform -rotate-90 drop-shadow-xl">
            {entries.map(([key, value], index) => {
              const percent = value / total;
              const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
              cumulativePercent += percent;
              const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
              const largeArcFlag = percent > 0.5 ? 1 : 0;
              const pathData = `M ${startX} ${startY} A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY} L 0 0`;
              const isActive = activeIndex === index;
              return (
                <path
                  key={key} d={pathData} fill={colors[index % colors.length]}
                  className="transition-all duration-300 ease-out cursor-pointer"
                  style={{ transform: isActive ? 'scale(1.1)' : 'scale(1)', transformOrigin: '0 0', opacity: activeIndex !== null && !isActive ? 0.6 : 1 }}
                  onMouseEnter={() => setActiveIndex(index)} onMouseLeave={() => setActiveIndex(null)}
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-24 h-24 md:w-28 md:h-28 bg-white dark:bg-slate-800 rounded-full flex flex-col items-center justify-center shadow-inner transition-all duration-300">
              <span className="text-2xl font-bold text-slate-800 dark:text-white transition-all duration-200">
                  {activeIndex !== null ? entries[activeIndex][1] : total}
              </span>
              <span className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider text-center px-2 truncate w-full">
                  {activeIndex !== null ? (CATEGORY_NAMES[entries[activeIndex][0]] || entries[activeIndex][0]) : 'Total'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex-1 w-full space-y-2 max-h-56 overflow-y-auto custom-scrollbar pr-2">
          {entries.map(([key, value], index) => (
            <div key={key} className={`flex items-center gap-3 p-2 rounded-lg transition-all cursor-pointer ${activeIndex === index ? 'bg-slate-100 dark:bg-slate-700/50' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} onMouseEnter={() => setActiveIndex(index)}>
              <div className={`w-3 h-3 md:w-4 md:h-4 rounded-full shadow-sm flex-shrink-0 transition-transform ${activeIndex === index ? 'scale-125' : ''}`} style={{ backgroundColor: colors[index % colors.length] }} />
              <span className={`text-xs md:text-sm flex-1 truncate transition-colors ${activeIndex === index ? 'text-slate-900 dark:text-white font-bold' : 'text-slate-600 dark:text-slate-400'}`}>
                {CATEGORY_NAMES[key] || key}
              </span>
              <span className="text-xs md:text-sm font-bold text-slate-800 dark:text-white bg-white dark:bg-slate-600 px-2 py-0.5 rounded shadow-sm">
                {Math.round((value / total) * 100)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await statsAPI.get();
      setStats(res.data);
    } catch (err) {
      console.error("Erro ao carregar estatísticas:", err);
      // Fallback seguro
      setStats({ total_jobs: 0, total_companies: 0, total_hired: 0, total_filled: 0, jobs_by_city: {}, jobs_by_area: {}, jobs_by_category: {}, weekly_growth: 0, avg_daily_jobs: 0 });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);
  const handleRefresh = () => { setIsRefreshing(true); fetchStats(); };

  if (loading && !isRefreshing) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Prepara dados de Categorias (Filtrando se quiser separar Vagas de Noticias)
  const categoryData = stats?.jobs_by_category || {};

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8 md:py-12 transition-colors duration-300">
      <div className="container mx-auto px-4 md:px-12 lg:px-20">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 md:mb-12 gap-6">
            <div className="text-center md:text-left">
                <span className="text-teal-600 dark:text-teal-400 font-bold text-xs tracking-[0.2em] uppercase flex items-center justify-center md:justify-start gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                    </span>
                    Dados em tempo real
                </span>
                <h1 className="text-3xl md:text-5xl font-bold font-heading text-slate-800 dark:text-white mt-2">
                    Panorama de Oportunidades
                </h1>
            </div>
            <button onClick={handleRefresh} disabled={isRefreshing} className="bg-white dark:bg-slate-800 p-3 rounded-full shadow-md text-slate-500 hover:text-teal-500 transition-all hover:rotate-180 disabled:animate-spin">
                <RefreshCw size={20} />
            </button>
        </div>

        {/* 1. CARDS DE DESTAQUE */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
          <StatCard delay={0} icon={Briefcase} value={stats.total_jobs} label="Total de Posts" trend={stats.weekly_growth} gradient="bg-gradient-to-br from-teal-500 to-teal-600" />
          <StatCard delay={100} icon={Building2} value={stats.total_companies} label="Empresas/Fontes" gradient="bg-gradient-to-br from-[#003b4a] to-[#004d5a]" />
          <StatCard delay={200} icon={FileText} value={categoryData['noticia'] || 0} label="Notícias/Editais" gradient="bg-gradient-to-br from-cyan-600 to-blue-600" />
          <StatCard delay={300} icon={Target} value={stats.total_filled} label="Vagas Preenchidas" gradient="bg-gradient-to-br from-lime-500 to-lime-600" />
        </div>

        {/* 2. GRÁFICOS PRINCIPAIS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
          {/* Gráfico de Categorias (NOVO) */}
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
              <InteractivePieChart data={stats.jobs_by_category} title="Distribuição por Categoria" icon={PieChart} />
          </div>
          {/* Gráfico de Cidades */}
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300">
              <InteractiveBarChart data={stats.jobs_by_city} title="Vagas por Cidade" icon={MapPin} />
          </div>
        </div>

        {/* 3. GRÁFICO DE ÁREAS (Full Width) */}
        <div className="mb-12 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-400">
             <InteractiveBarChart data={stats.jobs_by_area} title="Vagas por Área de Atuação" icon={Briefcase} />
        </div>

        {/* 4. MÉTRICAS EXTRAS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg group hover:scale-[1.02] transition-transform">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3"><Zap className="w-5 h-5" /><span className="text-lg font-medium">Fluxo Diário</span></div>
              <p className="text-5xl font-bold mb-1"><CountUp end={stats.avg_daily_jobs} /></p>
              <p className="text-white/80 text-sm">novos itens/dia</p>
            </div>
          </div>
          
          <div className="relative overflow-hidden bg-gradient-to-br from-[#003b4a] to-[#004d5a] rounded-2xl p-6 text-white shadow-lg group hover:scale-[1.02] transition-transform">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3"><TrendingUp className="w-5 h-5" /><span className="text-lg font-medium">Crescimento</span></div>
              <p className="text-5xl font-bold mb-1">+<CountUp end={stats.weekly_growth} />%</p>
              <p className="text-white/80 text-sm">última semana</p>
            </div>
          </div>
          
          <div className="relative overflow-hidden bg-gradient-to-br from-lime-500 to-lime-600 rounded-2xl p-6 text-[#003b4a] shadow-lg group hover:scale-[1.02] transition-transform">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3"><Award className="w-5 h-5" /><span className="text-lg font-medium">Alcance</span></div>
              <p className="text-5xl font-bold mb-1"><CountUp end={stats.jobs_by_city ? Object.keys(stats.jobs_by_city).length : 0} /></p>
              <p className="text-[#003b4a]/80 text-sm">cidades ativas</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StatsPage;